import {
  CancelOutlined,
  CheckCircleOutlined,
  DoneAll
} from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import { Chip, IconButton, Link, Stack, Tooltip } from '@mui/material'
import { green, red } from '@mui/material/colors'
import { format, parseISO } from 'date-fns'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import { DEFAULT_DATETIME_FORMAT } from '../../../../app/constants'
import EnhancedTable from '../../../../components/EnhancedTable'
import { bulkCompleteCallBacks, loadCallBacks } from '../../calendar/api'
import { loadUsers } from '../../users/api'
import { selectUserEntities } from '../../users/slice'
import { CallBackDialog } from '../call-backs/components'

const columns = [
  {
    id: 'call_date',
    label: 'Date',
    style: {
      minWidth: '200px'
    },
    sortable: true,
    cellRenderer: (datum) => format(parseISO(datum), DEFAULT_DATETIME_FORMAT)
  },
  {
    id: 'organization',
    label: 'Organization',
    style: {
      minWidth: '200px'
    },
    cellRenderer: (datum, row) =>
      datum ? (
        <Link
          component={RouterLink}
          color="text.primary"
          to={`/organizations/${datum}`}
        >
          {_.get(row, 'organization_name', 'Unknown')}
        </Link>
      ) : (
        'None'
      )
  },
  {
    id: 'product',
    label: 'Product',
    style: {
      minWidth: '200px'
    },
    cellRenderer: (datum) =>
      datum ? (
        <Link
          component={RouterLink}
          color="text.primary"
          to={`/common-types/products?selected=${datum.id}`}
        >
          {_.get(datum, 'name', 'Unknown')}
        </Link>
      ) : (
        'None'
      )
  },
  {
    id: 'notes',
    label: 'Notes',
    style: {
      minWidth: '150px',
      maxWidth: '300px'
    },
    cellRenderer: (datum) => datum || ''
  },
  {
    id: 'call_type',
    label: 'Type',
    style: {
      minWidth: '170px'
    },
    sortable: true,
    cellRenderer: (datum, row) => {
      return (
        <Stack direction="row" spacing={1}>
          <Chip size="small" label={row.call_type_name} />
          {row.is_urgent && <Chip size="small" color="error" label="Urgent" />}
          {row.is_complete && (
            <Chip size="small" color="success" label="Complete" />
          )}
        </Stack>
      )
    }
  },
  {
    id: 'is_complete',
    label: 'Status',
    style: {
      minWidth: '100px'
    },
    sortable: true,
    align: 'center',
    cellRenderer: (datum) => {
      const StatusIcon = datum ? CheckCircleOutlined : CancelOutlined
      return (
        <StatusIcon
          fontSize="small"
          sx={{
            fill: datum ? green[400] : red[400],
            mr: 0.5
          }}
        />
      )
    }
  }
]

function OrganizationCallBacksView() {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const table = useRef()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [focusedId, setFocusedId] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const users = useSelector(selectUserEntities)

  useEffect(() => {
    dispatch(loadUsers())
  }, [])

  const getDataFetcher = () => (queryParams) =>
    dispatch(loadCallBacks({ ...queryParams })).unwrap()

  const handleCompleteSelected = () => {
    if (selectedIds.length === 0) return
    dispatch(bulkCompleteCallBacks(selectedIds))
      .unwrap()
      .then((res) => {
        enqueueSnackbar(
          `${res.data?.updated || selectedIds.length} callback(s) marked complete`,
          { variant: 'success' }
        )
        setSelectedIds([])
        if (table?.current) {
          table.current.loadData()
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to complete callbacks', { variant: 'error' })
      })
  }

  return (
    <Fragment>
      <EnhancedTable
        ref={table}
        title="Call Backs"
        editable
        selectable
        hoverable
        searchable
        dateRange
        dateKeyword="call_date"
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={(start, end) => {
          setStartDate(start)
          setEndDate(end)
        }}
        columns={columns}
        fetchData={getDataFetcher()}
        defaultOrderBy="call_date"
        defaultOrder="asc"
        onEdit={(row) => {
          setFocusedId(row.id)
          setIsDialogOpen(true)
        }}
        onSelectedChange={(selected) => setSelectedIds(selected)}
        actions={[
          selectedIds.length > 0 && (
            <Tooltip key="complete-selected" title="Complete All Selected">
              <IconButton color="success" onClick={handleCompleteSelected}>
                <DoneAll />
              </IconButton>
            </Tooltip>
          ),
          <Tooltip key="new" title="Add New">
            <IconButton
              onClick={() => {
                setFocusedId(null)
                setIsDialogOpen(true)
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ].filter(Boolean)}
        filters={[
          {
            id: 'is_complete',
            group: 'Call Back Filters',
            label: 'Status',
            type: 'select',
            getParamValue: (option) => (option === 0 ? null : option === 1),
            options: [
              { label: 'All', value: 0 },
              { label: 'Complete', value: 1 },
              { label: 'Incomplete', value: 2 }
            ],
            defaultValue: 2
          },
          {
            id: 'is_urgent',
            group: 'Call Back Filters',
            label: 'Urgent',
            type: 'select',
            getParamValue: (option) => (option === 0 ? null : option === 1),
            options: [
              { label: 'All', value: 0 },
              { label: 'Yes', value: 1 },
              { label: 'No', value: 2 }
            ],
            defaultValue: 0
          },
          {
            id: 'user',
            group: 'Call Back Filters',
            label: 'User',
            type: 'select',
            getParamValue: (option) => (option === -1 ? null : option),
            options: [{ label: 'All', value: -1 }].concat(
              Object.values(users.entities).map((t) => ({
                label: `${t.first_name} ${t.last_name}`,
                value: t.id
              }))
            ),
            defaultValue: -1
          }
        ]}
      />
      <CallBackDialog
        open={isDialogOpen}
        callBackId={focusedId}
        onSubmit={() => {
          if (table?.current) {
            table.current.loadData()
          }
        }}
        onClose={() => setIsDialogOpen(false)}
      />
    </Fragment>
  )
}

export default OrganizationCallBacksView
